import fetch, { Request } from "cross-fetch";
import settings from "../settings";
import useClientContext from "./useClientContext";
import useSession from "./useSession";
const { API_BACKEND_HOSTNAME } = settings;

const apiEntryTokenHeaderName = "x-sti-auth-entry-token";
const apiUserTokenHeaderName = "x-sti-auth-user-token";
const defaultHeaders = {
  "content-type": "application/json; charset=utf-8",
};
const defaultParams = {
  method: "GET",
  headers: defaultHeaders,
};
const staticRouteTest = /^\/api\/static-/;

interface ApiFetchOptions {
  applySessionHeaders?: boolean;
}

/**
 * Hook that provides a fetch function that adds required headers and values for our backend API.
 * @example
 * import useApiFetch from "@hooks/useApiFetch";
 * ...
 * const { fetchApiEndpoint } = useApiFetch();
 */
export default function useApiFetch(options?: ApiFetchOptions) {
  const { data: session } = useSession();
  const [{ currentEntry }] = useClientContext();
  const { applySessionHeaders = true } = options || {};

  function addApiToken(endpoint: RequestInfo, params?: RequestInit) {
    const apiToken = session.user.token;
    const entryToken = currentEntry?.token;

    if (apiToken && !staticRouteTest.test(`${endpoint}`)) {
      const headers = Object.assign({ ...defaultHeaders }, params.headers || {});

      // add both API tokens to requests.
      headers[apiUserTokenHeaderName] = apiToken;
      if (entryToken) {
        headers[apiEntryTokenHeaderName] = entryToken;
      }
      params.headers = headers;
    }
    return params;
  }

  function fetchApiEndpoint<T>(endpoint: RequestInfo, params?: RequestInit): Promise<T> {
    const newParams = Object.assign(
      { ...defaultParams },
      applySessionHeaders ? addApiToken(endpoint, params) : {},
    );
    const req = new Request(`${API_BACKEND_HOSTNAME}${endpoint}`);

    return fetch(req, newParams)
      .then((res) => {
        return res
          .text()
          .then((text) => {
            // to resolve any body-less responses from our API:
            try {
              const data: T | ApiBackendError = JSON.parse(text);
              if (res.status > 400) {
                if ((data as ApiBackendError).errors) {
                  throw new Error(
                    `${
                      Object.entries((data as ApiBackendError).errors)[0]
                        .flat()
                        .join(" ") ?? "Something went wrong. Please try again."
                    }`,
                  );
                } else {
                  throw new Error(`Error: Something went wrong. Please try again.`);
                }
              }
              return data as T;
            } catch (error) {
              if (!`${res.status}`.startsWith("20")) {
                throw error;
              }
            }
          })
          .catch((err) => {
            throw err;
          });
      })
      .catch((err) => {
        throw err;
      });
  }

  return { fetchApiEndpoint };
}

interface ApiBackendError {
  errors: {
    [property: string]: string[];
  };
}
